import Phaser from 'phaser';
import type { Room } from '@colyseus/sdk';
import { reconcile } from '@rabbit2/shared/sim';
import { joinCoop } from '../net/coopRoom.ts';

const HERO_SPEED = 190; // px/s (Baseline A.2) — predição local do próprio herói (AC-M2-05)

interface HeroNetView {
  x: number;
  y: number;
  hp: number;
  down: boolean;
}

/**
 * CoopScene (M2) — cliente do RunRoom AUTORITATIVO: envia inputs e renderiza os heróis a partir
 * do estado do servidor. (Predição plena/reconciliação = AC-M2-05; aqui: servidor manda, cliente mostra.)
 */
export class CoopScene extends Phaser.Scene {
  #room: Room | null = null;
  #myIdx = -1;
  #g!: Phaser.GameObjects.Graphics;
  #hud!: Phaser.GameObjects.Text;
  #keys!: Record<'up' | 'down' | 'left' | 'right', Phaser.Input.Keyboard.Key>;
  #seq = 0;
  #seed = 1;
  #players = 2;
  #erro = '';
  #pred: { x: number; y: number } | null = null; // predição local do próprio herói

  constructor() {
    super('coop');
  }

  init(data: { seed?: number; players?: number }): void {
    this.#seed = data.seed ?? 1;
    this.#players = Math.max(1, Math.min(4, data.players ?? 2));
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#1A1633');
    this.cameras.main.setZoom(0.5);
    this.cameras.main.centerOn(960, 544); // arena 1920×1088 cabe no viewport a 0,5×
    this.#g = this.add.graphics();
    this.#hud = this.add.text(8, 8, 'conectando…', {
      fontFamily: 'monospace', fontSize: '16px', color: '#FFF3E0',
    }).setScrollFactor(0).setDepth(100);

    const kb = this.input.keyboard;
    if (kb) {
      this.#keys = { up: kb.addKey('W'), down: kb.addKey('S'), left: kb.addKey('A'), right: kb.addKey('D') };
      kb.addKeys('UP,DOWN,LEFT,RIGHT');
    }

    this.installTestApi();

    joinCoop({ seed: this.#seed, playerCount: this.#players })
      .then((room) => {
        this.#room = room;
        this.#myIdx = -1;
        room.onStateChange(() => {
          const me = room.state.players.get(room.sessionId);
          if (me) this.#myIdx = me.heroIdx;
        });
        this.game.canvas.setAttribute('data-ready', 'true');
      })
      .catch((e: unknown) => {
        this.#erro = e instanceof Error ? e.message : String(e);
        this.game.canvas.setAttribute('data-ready', 'true');
      });
  }

  #input(): { mx: number; my: number } {
    const kb = this.input.keyboard;
    const dn = (c: string): boolean => (kb ? kb.checkDown(kb.addKey(c)) : false);
    let mx = 0;
    let my = 0;
    if (this.#keys) {
      if (this.#keys.left.isDown || dn('LEFT')) mx -= 1;
      if (this.#keys.right.isDown || dn('RIGHT')) mx += 1;
      if (this.#keys.up.isDown || dn('UP')) my -= 1;
      if (this.#keys.down.isDown || dn('DOWN')) my += 1;
    }
    return { mx, my };
  }

  override update(_t: number, dtMs: number): void {
    const room = this.#room;
    if (!room) {
      this.#hud.setText(this.#erro ? `erro: ${this.#erro}` : 'conectando…');
      return;
    }
    const { mx, my } = this.#input();
    room.send('input', { seq: ++this.#seq, mx, my });

    const heroes = heroesOf(room);
    // predição local do próprio herói (AC-M2-05): aplica o input já, reconcilia com o servidor
    const mine = this.#myIdx >= 0 ? heroes[this.#myIdx] : undefined;
    if (mine) {
      if (!this.#pred) this.#pred = { x: mine.x, y: mine.y };
      const dt = Math.min(0.05, dtMs / 1000);
      const len = Math.sqrt(mx * mx + my * my) || 1;
      const nx = len > 1 ? mx / len : mx;
      const ny = len > 1 ? my / len : my;
      this.#pred.x += nx * HERO_SPEED * dt;
      this.#pred.y += ny * HERO_SPEED * dt;
      this.#pred = reconcile(this.#pred, { x: mine.x, y: mine.y }); // suaviza contra o autoritativo
    }

    this.#g.clear();
    heroes.forEach((h, i) => {
      const own = i === this.#myIdx && this.#pred;
      const px = own ? this.#pred!.x : h.x;
      const py = own ? this.#pred!.y : h.y;
      this.#g.fillStyle(h.down ? 0x4a4173 : i === this.#myIdx ? 0xff8a3d : 0xfff3e0, 1);
      this.#g.fillCircle(px, py, 16);
    });
    const nPlayers = room.state?.players?.size ?? 0;
    this.#hud.setText(
      `co-op · jogadores ${nPlayers}/${this.#players} · tick ${room.state?.tick ?? 0} · eu=herói ${this.#myIdx}`,
    );
  }

  private installTestApi(): void {
    window.__rabbitTest = {
      ...(window.__rabbitTest ?? {}),
      scene: () => 'coop',
      fps: () => this.game.loop.actualFps,
      seed: () => this.#seed,
      rngSample: window.__rabbitTest?.rngSample ?? (() => []),
      coop: () => {
        const room = this.#room;
        return {
          connected: !!room,
          players: room?.state?.players?.size ?? 0,
          myIdx: this.#myIdx,
          tick: room?.state?.tick ?? 0,
          heroes: room ? heroesOf(room).map((h) => ({ x: h.x, y: h.y })) : [],
          erro: this.#erro,
        };
      },
    };
  }
}

function heroesOf(room: Room): HeroNetView[] {
  const out: HeroNetView[] = [];
  room.state?.heroes?.forEach((h: HeroNetView) => out.push({ x: h.x, y: h.y, hp: h.hp, down: h.down }));
  return out;
}
